import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { api, resolveImg } from "@/lib/api";
import { ArrowUpRight, ArrowLeft, ArrowRight, Play, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import ProjectOverlay from "@/components/site/ProjectOverlay";
import VideoLightbox from "@/components/site/VideoLightbox";

function youTubeId(url = "") {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}
function vimeoId(url = "") {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

function ShowcaseMedia({ project, muted }) {
  const iframeRef = useRef(null);
  const videoRef = useRef(null);
  const video = (project.videos || []).filter(Boolean)[0];
  const yt = video && youTubeId(video);
  const vm = video && vimeoId(video);

  // control sound for native <video>
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, video]);

  // reliably kick off muted autoplay for native <video> (some browsers ignore the autoPlay attr)
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    const tryPlay = () => { const p = el.play(); if (p && p.catch) p.catch(() => {}); };
    tryPlay();
    el.addEventListener("canplay", tryPlay);
    el.addEventListener("loadeddata", tryPlay);
    return () => {
      el.removeEventListener("canplay", tryPlay);
      el.removeEventListener("loadeddata", tryPlay);
    };
  }, [video]);

  // control sound for YouTube via the IFrame JS API (postMessage)
  useEffect(() => {
    if (!yt) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      JSON.stringify({ event: "command", func: muted ? "mute" : "unMute", args: [] }),
      "*"
    );
  }, [muted, yt]);

  if (video && (yt || vm)) {
    const src = yt
      ? `https://www.youtube.com/embed/${yt}?autoplay=1&mute=1&loop=1&playlist=${yt}&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1`
      : `https://player.vimeo.com/video/${vm}?autoplay=1&muted=1&loop=1&background=1`;
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <iframe
          ref={iframeRef}
          title={project.title}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full"
          style={{ width: "177.78vh", height: "56.25vw" }}
          src={src}
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      </div>
    );
  }
  if (video) {
    return (
      <video
        ref={videoRef}
        key={video}
        className="absolute inset-0 h-full w-full object-cover"
        src={resolveImg(video)}
        poster={resolveImg(project.cover_image)}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />
    );
  }
  return (
    <img src={resolveImg(project.cover_image)} alt={project.title} className="absolute inset-0 h-full w-full object-cover" />
  );
}

export default function SelectedWork({ data, projects = [] }) {
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [idx, setIdx] = useState(0);
  const [openIndex, setOpenIndex] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (active === "all" ? projects : projects.filter((p) => p.category === active)),
    [active, projects]
  );

  useEffect(() => { setIdx(0); }, [active]);
  // re-mute whenever the shown project changes so sound never plays unexpectedly
  useEffect(() => { setMuted(true); }, [idx, active]);

  const tabs = [{ name: "All", slug: "all" }, ...categories];
  const current = filtered[idx];
  const many = filtered.length > 1;
  const hasVideo = current && (current.videos || []).filter(Boolean).length > 0;
  const prev = (e) => { e?.stopPropagation(); setIdx((n) => (n - 1 + filtered.length) % filtered.length); };
  const next = (e) => { e?.stopPropagation(); setIdx((n) => (n + 1) % filtered.length); };
  const openCurrent = () => {
    if (hasVideo) { setMuted(true); setVideoOpen(true); }
    else setOpenIndex(idx);
  };

  return (
    <section id="work" data-testid="work-section" className="bg-background py-24 md:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-accent">Selected Work</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-serif font-light text-5xl sm:text-6xl md:text-7xl tracking-tight leading-none">
                Signature projects
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/projects"
              data-testid="all-projects-link"
              className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-foreground transition-colors"
            >
              All Projects
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>

        {/* filter tabs */}
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 md:gap-x-12 border-b border-foreground/15 pb-4 mb-10 md:mb-12" data-testid="work-filters">
          {tabs.map((t) => (
            <FilterTab key={t.slug} label={t.name} value={t.slug} active={active} onClick={setActive} />
          ))}
        </div>

        {/* full-width showcase */}
        {current ? (
          <div className="relative">
            <div
              role="button"
              tabIndex={0}
              onClick={openCurrent}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openCurrent()}
              data-testid={`project-card-${current.slug}`}
              className="group relative block w-full overflow-hidden cursor-pointer bg-muted"
            >
              <div className="relative h-[440px] sm:h-[560px] md:h-[720px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    className="absolute inset-0"
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <ShowcaseMedia project={current} muted={muted} />
                  </motion.div>
                </AnimatePresence>
              </div>

              {hasVideo && (
                <span
                  data-testid="video-badge"
                  className="absolute top-5 left-5 md:top-6 md:left-6 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/45 backdrop-blur-sm px-3 py-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-white"
                >
                  <Play size={13} weight="fill" />
                  Video
                </span>
              )}

              {hasVideo && (
                <button
                  data-testid="sound-toggle"
                  onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
                  aria-label={muted ? "Unmute video" : "Mute video"}
                  className="absolute top-5 right-5 md:top-6 md:right-6 z-20 h-10 w-10 md:h-11 md:w-11 rounded-full bg-black/45 backdrop-blur-sm text-white grid place-items-center transition-all duration-300 hover:bg-black/65 hover:scale-105"
                >
                  {muted ? <SpeakerSlash size={18} weight="light" /> : <SpeakerHigh size={18} weight="light" />}
                </button>
              )}

              <AnimatePresence>
                {hasVideo && muted && (
                  <motion.button
                    key="sound-hint"
                    data-testid="sound-hint"
                    onClick={(e) => { e.stopPropagation(); setMuted(false); }}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="absolute top-[4.25rem] right-5 md:top-[5rem] md:right-6 z-20 inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-sm px-3.5 py-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-white/90 hover:bg-black/60 transition-colors duration-300"
                  >
                    <motion.span
                      animate={{ opacity: [0.55, 1, 0.55] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                      className="inline-flex"
                    >
                      <SpeakerHigh size={14} weight="light" />
                    </motion.span>
                    Tap for sound
                  </motion.button>
                )}
              </AnimatePresence>

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-black/0 opacity-90" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-12 flex items-end justify-between">
                <div>
                  <p className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/75">{current.category_name || "Project"}</p>
                  <h3 data-testid="showcase-title" className="mt-3 font-serif font-light text-3xl md:text-6xl text-white leading-none">{current.title}</h3>
                  {current.location && <p className="mt-3 text-sm md:text-base text-white/75">{current.location}</p>}
                </div>
                <span className="hidden md:grid shrink-0 h-14 w-14 rounded-full border border-white/50 place-items-center text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <ArrowUpRight size={22} />
                </span>
              </div>

              {many && (
                <>
                  <button
                    data-testid="showcase-prev"
                    onClick={prev}
                    className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 h-11 w-11 md:h-14 md:w-14 rounded-full bg-black/40 backdrop-blur-sm text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/60 hover:scale-105"
                    aria-label="Previous project"
                  >
                    <ArrowLeft size={20} weight="light" />
                  </button>
                  <button
                    data-testid="showcase-next"
                    onClick={next}
                    className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20 h-11 w-11 md:h-14 md:w-14 rounded-full bg-black/40 backdrop-blur-sm text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-black/60 hover:scale-105"
                    aria-label="Next project"
                  >
                    <ArrowRight size={20} weight="light" />
                  </button>
                </>
              )}
            </div>

            {/* dots / index */}
            {many && (
              <div className="mt-6 flex items-center justify-center gap-3">
                {filtered.map((p, k) => (
                  <button
                    key={p.id}
                    data-testid={`showcase-dot-${k}`}
                    onClick={() => setIdx(k)}
                    aria-label={`Go to ${p.title}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${k === idx ? "w-8 bg-foreground" : "w-2.5 bg-foreground/30 hover:bg-foreground/60"}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-foreground/50 py-20" data-testid="work-empty">No projects in this category yet.</p>
        )}
      </div>

      {videoOpen && current && (
        <VideoLightbox project={current} onClose={() => setVideoOpen(false)} />
      )}

      {openIndex !== null && filtered.length > 0 && (
        <ProjectOverlay
          projects={filtered}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </section>
  );
}

function FilterTab({ label, value, active, onClick }) {
  const is = active === value;
  return (
    <button
      data-testid={`work-filter-${value}`}
      onClick={() => onClick(value)}
      className="relative pb-1 text-[11px] uppercase tracking-[0.25em] transition-colors duration-300"
      style={{ color: is ? "hsl(var(--foreground))" : "hsl(var(--foreground) / 0.5)" }}
    >
      {label}
      {is && <motion.span layoutId="work-filter-underline" className="absolute left-0 right-0 -bottom-4 h-[2px] bg-accent" />}
    </button>
  );
}
